<script>
import UploadList from './upload-list'
import Upload from './upload'
import { uploadToOSS } from '@/utils/utils'

const noop = function () {}
const FILE_LIST_CONFIG = {
  showList: false, // 是否展示文件列表
  showIndex: false, // 是否展示序号列
  showFileType: true, // 是否展示文件类型列
  showFileSize: true, // 是否展示文件大小列
  showClearAllFilesButton: false, // 是否开启清空列表按钮
  clearAllFilesButtonText: '清空列表', // 清空列表按钮展示文字
  secondaryConfirmation: false, // 清空列表时是否二次确认
  secondaryConfirmationText: '确定清空所有文件吗?' // 清空列表时二次确认文字提示
}

/**
 * 上传文件组件入口
 * 对外提供方法：
 *   clearFiles - 清空当前用户选择的所有文件，包含所有状态(ready, fail, success)
 *   submit - 上传文件的方法
 *   getFileList - 获取当前用户选择的所有文件列表
 * 对外提供Slots：
 *   trigger - 选取文件DOM结构
 *   folderTrigger - 选取文件夹DOM结构
 *   tip - 提示文字
 *   注意: trigger与folderTrigger至少要有一个
 */

export default {
  name: 'JyMultipleUpload',

  components: {
    UploadList,
    Upload
  },

  props: {
    showFileListConfig: { // 文件列表配置项
      type: Object,
      default: () => FILE_LIST_CONFIG
    },
    limitSize: { // 允许上传的文件字节数最大值
      type: Number,
      default: 0
    },
    accept: String, // 允许的文件类型, 例如 'image/*' 等
    acceptTypes: { // 允许上传的具体文件类型
      type: Array,
      default: () => []
    },
    onUnexpectedFileType: { // 文件类型超出限制钩子
      type: Function,
      default: noop
    },
    onUnexpectedFileSize: { // 文件大小超出限制钩子
      type: Function,
      default: noop
    },
    autoUpload: { // 是否选择完图片后立即自动上传, true为自动上传, 反之不会自动上传
      type: Boolean,
      default: true
    },
    limit: Number, // 限制一次选择文件的数量
    onExceed: { // 文件数量超出限制的钩子
      type: Function,
      default: noop
    },
    onError: {
      type: Function,
      default: noop
    },
    uploadToFileServerFirst: { // 是否先将原文件上传至文件服务器, true为上传至文件服务器, 反之不上传
      type: Boolean,
      default: true
    },
    uploadToFileServerMethod: { // 上传至文件服务器的方法, 返回值必须为promise实例, 请参考/src/views/sample-page/sample-jy-multiple-upload.vue文件中的 fakeUploadToOSS 方法
      type: Function,
      default: uploadToOSS
    },
    httpRequest: Function // 上传至接口的方法, 返回值必须为promise实例, 请参考/src/views/sample-page/sample-jy-multiple-upload.vue文件中的 fakeHttpRequest 方法
    // uploadSingleFileOneTime: { // TODO: implement process fileList function later
    //   type: Boolean,
    //   default: true
    // }
  },

  data () {
    return {
      uploadFiles: [],
      fileListConfig: {},
      tempIndex: 1,
      multiple: true,
      isLoading: false,
      uploadSingleFileOneTime: true // TODO: add to props and remove me later
    }
  },

  watch: {
    showFileListConfig: {
      handler (newValue, oldValue) {
        if (newValue) {
          this.fileListConfig = {...FILE_LIST_CONFIG, ...newValue}
        }
      },
      immediate: true
    }
  },

  created () {
    if (!this.httpRequest) {
      throw new Error('httpRequest is required!')
    }
  },

  methods: {
    handleStart (rawFile) {
      rawFile.uid = Date.now() + this.tempIndex++
      let file = {
        status: 'ready', // ready, fail, success
        name: rawFile.name,
        size: rawFile.size,
        uid: rawFile.uid,
        type: rawFile.type,
        tipText: '待上传',
        raw: rawFile
      }

      this.uploadFiles.push(file)
    },

    clearFiles () {
      if (this.isLoading) {
        return false
      }

      this.uploadFiles = []
      return true
    },

    submit () {
      if (this.isLoading) {
        return
      }
      this.$refs['upload-inner'].beforeUpload(this.uploadFiles)
    },

    // 改变组件内部isLoading状态
    switchLoadingStatus (isLoading) {
      this.isLoading = isLoading
      this.$emit('set-loading', isLoading)
    },

    // 上传之后更新文件信息
    updateFileStatus (params) {
      let { index, file } = params

      if (this.uploadFiles[index].uid === file.uid) {
        this.uploadFiles[index] = file
        this.uploadFiles = [].concat(this.uploadFiles)
      }
    },

    getFileList () {
      return this.uploadFiles || []
    },

    // 重新上传单个文件
    reloadSingleFile (uid) {
      this.$refs['upload-inner'].beforeUpload(this.uploadFiles.filter(it => it.uid === uid))
    },

    // 删除单个文件
    deleteSingleFile (uid) {
      let idx = this.uploadFiles.findIndex(it => it.uid === uid)

      if (idx === -1) return

      this.uploadFiles.splice(idx, 1)
      this.uploadFiles = [].concat(this.uploadFiles)
    }
  },

  render (h) {
    let uploadList

    if (this.fileListConfig && this.fileListConfig.showList) {
      uploadList = (
        <UploadList
          files={this.uploadFiles}
          fileListConfig={this.fileListConfig}
          isLoading={this.isLoading}
          on-reload-single-file={this.reloadSingleFile}
          on-delete-single-file={this.deleteSingleFile}
          on-clear-all-files={this.clearFiles}>
        </UploadList>
      )
    }

    const uploadData = {
      props: {
        multiple: this.multiple,
        accept: this.accept,
        fileList: this.uploadFiles,
        autoUpload: this.autoUpload,
        limit: this.limit,
        uploadToFileServerFirst: this.uploadToFileServerFirst,
        uploadSingleFileOneTime: this.uploadSingleFileOneTime,
        acceptTypes: this.acceptTypes,
        limitSize: this.limitSize,
        'on-unexpected-file-type': this.onUnexpectedFileType,
        'on-unexpected-file-size': this.onUnexpectedFileSize,
        'on-exceed': this.onExceed,
        'on-start': this.handleStart,
        'on-error': this.onError,
        'upload-to-file-server-method': this.uploadToFileServerMethod,
        'http-request': this.httpRequest,
        isLoading: this.isLoading
      },
      on: {
        'switch-loading-status': this.switchLoadingStatus,
        'update-file-status': this.updateFileStatus
      },
      ref: 'upload-inner'
    }

    const trigger = this.$slots.trigger ? (<template slot="trigger">{this.$slots.trigger}</template>) : '' // 选取文件
    const folderTrigger = this.$slots.folderTrigger ? (<template slot="folderTrigger">{this.$slots.folderTrigger}</template>) : '' // 选取文件夹
    const uploadComponent = <upload {...uploadData}>{[trigger, folderTrigger]}</upload>

    return (
      <div class="upload-main">
        { uploadComponent }
        { this.$slots.tip }
        { uploadList }
        { this.$slots.default }
      </div>
    )
  }
}
</script>

<style lang="less" scoped>
.upload-main {
  padding: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/jy-multiple-upload/src/index.vue